import { unified, Plugin } from 'unified';
import markdown from 'remark-parse';
import remark2rehype from 'remark-rehype';
import html from 'rehype-stringify';
import emoji from 'remark-emoji';
import rehypeUrls from 'rehype-urls';
import { visit } from 'unist-util-visit';
import { MarkdownParser, Config } from '@sdk/components/richText/helpers/parseMarkdownToHtml.types';
import parseMentionsToHtml from '@sdk/components/richText/helpers/parseMentionsToHtml';
import gettext from '@sdk/lang/gettext';
import {
  getRehypeUrlsOptions,
  paragraphsToDivs,
  slingMarkdownSyntaxPlugin,
} from '@sdk/components/richText/helpers/parseMarkdownToHtml';

export const getMessagesParser =
  (editedDateLabel: string | null): MarkdownParser =>
  (text, config = {}) => {
    return parseMessageToHtml(text, config, editedDateLabel);
  };

export const parseMessageToHtml = (
  text: string,
  config: Config = {},
  editedDateLabel: string | null
) => {
  const textToParse = config.parseMentions ? parseMentionsToHtml(text, config) : text;
  const rehypeUrlsOptions = getRehypeUrlsOptions(config.disableRedirects);

  const processor = unified()
    .use(markdown)
    .use(emoji, { emoticon: true })
    .use(slingMarkdownSyntaxPlugin)
    .use(remark2rehype)
    .use(rehypeUrls, rehypeUrlsOptions)
    .use(markMessageAsEditedPlugin, { editedDateLabel })
    .use(paragraphsToDivs)
    .use(html);

  const parsed = processor.processSync(textToParse);

  return parsed.value as string;
};

export const markMessageAsEditedPlugin: Plugin<[{ editedDateLabel: string | null }]> = ({
  editedDateLabel,
}) => {
  const editedMarkLeaf = {
    type: 'element',
    tagName: 'span',
    properties: {
      className: 'edited-message-label',
      dataFor: 'edited-date-label-tooltip',
      dataPlace: 'top',
      dataTip: editedDateLabel,
    },
    children: [{ type: 'text', value: ` ${gettext('(edited)')}` }],
  };

  return (tree) => {
    if (!editedDateLabel) {
      return tree;
    }

    let editedMarkInserted = false;

    return visit(
      tree,
      (node: any) => node.type === 'element' && node.tagName === 'p',
      (node: any) => {
        if (editedMarkInserted) {
          return;
        }

        node.children.push(editedMarkLeaf);
        editedMarkInserted = true;
      },
      true
    );
  };
};
